import React from 'react';
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Box,
  Button,
  Chip
} from '@mui/material';
import {
  Favorite as FavoriteIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { MenuItem } from '../data/menuData';

interface MenuItemCardProps {
  item: MenuItem;
  onAddToCart: (item: MenuItem) => void;
  onCardClick: (item: MenuItem) => void;
}

const MenuItemCard: React.FC<MenuItemCardProps> = ({ item, onAddToCart, onCardClick }) => {
  const handleCardClick = (e: React.MouseEvent) => {
    onCardClick(item);
  };

  const handleAddClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onCardClick(item);
  };

  return (
    <Card
      onClick={handleCardClick}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'row',
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
        cursor: 'pointer'
      }}
    >
      {/* Text content (left) */}
      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: { xs: 1.5, md: 1.5 }, gap: 0.5 }}>
        <Typography
          variant="h6"
          component="h3"
          sx={{
            fontWeight: 700,
            fontSize: { xs: '1.1rem', md: '1rem' },
            lineHeight: '1.375rem',
            letterSpacing: '-0.0125rem',
            mb: 0.5,
            color: '#1a1a1a',
            display: '-webkit-box',
            WebkitLineClamp: 1,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden'
          }}
        >
          {item.name}
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 0.5 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 500,
              fontSize: { xs: '1.1rem', md: '1rem' },
              lineHeight: '1.375rem',
              letterSpacing: '-0.0125rem',
              color: '#000000',
            }}
          >
            ${item.price}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <FavoriteIcon sx={{ fontSize: 16, color: 'text.secondary', mr: 0.5 }} />
            <Typography variant="body2" color="text.secondary">
              {item.likes}
            </Typography>
          </Box>
        </Box>
        
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mb: 0,
            fontWeight: 500,
            fontSize: { xs: '0.9rem', md: '0.875rem' },
            lineHeight: { xs: '1.3rem', md: '1.25rem' },
            letterSpacing: '-0.00625rem',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden'
          }}
        >
          {item.description}
        </Typography>
      </CardContent>

      {/* Image (right) */}
      <Box sx={{ 
        position: 'relative', 
        width: { xs: 120, md: 180 }, 
        height: '100%',
        flexShrink: 0, 
        borderRadius: 2, 
        overflow: 'hidden',
        alignSelf: 'stretch'
      }}>
        <CardMedia
          component="img"
          image={item.image}
          alt={item.name}
          loading="lazy"
          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
        
        {/* Add to Cart Button */}
        <Box
          onClick={handleAddClick}
          sx={{ 
            position: 'absolute',
            bottom: 12,
            right: 12,
            width: { xs: 36, md: 44 },
            height: { xs: 36, md: 44 },
            borderRadius: '50%',
            backgroundColor: 'white',
            color: 'black',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: '#f5f5f5',
              transform: 'scale(1.05)',
              boxShadow: '0 6px 16px rgba(0,0,0,0.2)'
            },
            '&:active': {
              transform: 'scale(0.95)'
            }
          }}
        >
          <AddIcon sx={{ color: 'black', fontSize: { xs: 18, md: 20 } }} />
        </Box>
        
        {/* Cart quantity indicator */}
        {item.inCart > 0 && (
          <Chip
            label={item.inCart}
            size="small"
            sx={{
              position: 'absolute',
              bottom: 12,
              left: 12,
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              color: 'white'
            }}
          />
        )}
      </Box>
    </Card>
  );
};

export default MenuItemCard;
